import { Link } from "react-router-dom";
import { hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import { useApplications } from "./use-issuance";
import type { ExceptionType, PolicyApplication } from "./types";

// B5 — the issuance desk queue. Lists open applications (incl. exceptions), scope-
// filtered by the API. Exceptions float to the top; PAID_NOT_ISSUED (the customer paid
// the carrier but no policy issued) is the highest-priority, customer-protection case and
// is surfaced most prominently. RBAC is UX — the API enforces every read.

// The application status as a calm k-status pill. Premiums stay calm ink; only a genuine
// ISSUANCE_EXCEPTION / FAILED carries the red "ended" cue.
const STATUS_VARIANT: Record<string, string> = {
  DRAFT: "pending",
  SUBMITTED_TO_CARRIER: "review",
  ISSUANCE_PENDING: "review",
  ISSUED: "active",
  ISSUANCE_EXCEPTION: "ended",
  QUOTE_REVALIDATION: "review",
  WITHDRAWN: "pending",
  DECLINED: "pending",
  FAILED: "ended",
};

// Sort weight — lower sorts first. PAID_NOT_ISSUED is the top of the desk.
function priority(app: PolicyApplication): number {
  if (app.status !== "ISSUANCE_EXCEPTION") return 3;
  if (app.exceptionType === "PAID_NOT_ISSUED") return 0;
  return 1;
}

export function IssuanceHome() {
  const { t } = useT();
  const { me } = usePrincipal();
  const q = useApplications();

  if (!hasGrant(me, "application:read", "own")) {
    return (
      <section className="surface issuance">
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="lock" size={26} />
          </div>
          <p className="k-empty__title" role="note">
            {t("iss.restricted")}
          </p>
        </div>
      </section>
    );
  }

  const apps = [...(q.data ?? [])].sort((a, b) => priority(a) - priority(b));
  const exceptions = apps.filter((a) => a.status === "ISSUANCE_EXCEPTION");

  return (
    <section className="surface issuance">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("iss.heading")}</h1>
          {exceptions.length > 0 ? (
            <p
              className="surface__subtitle issuance-exception-count"
              role="status"
              data-testid="exception-count"
            >
              {t("iss.exceptionsWaiting")}: {exceptions.length}
            </p>
          ) : null}
        </div>
      </header>

      {q.isLoading ? (
        <div className="k-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("iss.loading")}</span>
          </div>
        </div>
      ) : null}
      {q.error ? (
        <div className="k-note k-note--error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <p role="alert" className="k-note__desc" data-testid="issuance-error">
              {t("iss.error")}
            </p>
          </div>
        </div>
      ) : null}

      {!q.isLoading && apps.length === 0 ? (
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="document" size={26} />
          </div>
          <p className="k-empty__title" data-testid="issuance-empty">
            {t("iss.empty")}
          </p>
        </div>
      ) : (
        <ul className="issuance-list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {apps.map((a) => {
            const isPaidNotIssued =
              a.status === "ISSUANCE_EXCEPTION" && a.exceptionType === "PAID_NOT_ISSUED";
            return (
              <li
                key={a.id}
                className={`k-card k-card--pad-sm issuance-row${isPaidNotIssued ? " issuance-row--critical" : ""}`}
                data-testid={`app-row-${a.id}`}
                data-critical={isPaidNotIssued ? "true" : undefined}
                style={{
                  padding: 0,
                  marginBottom: 12,
                  ...(isPaidNotIssued
                    ? {
                        borderColor: "var(--k-danger-border)",
                        borderLeft: "3px solid var(--k-danger)",
                      }
                    : {}),
                }}
              >
                <Link
                  to={`/issuance/${a.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "16px 20px",
                    textDecoration: "none",
                  }}
                >
                  <span
                    className="issuance-row-carrier"
                    style={{ fontWeight: 600, color: "var(--k-ink)" }}
                  >
                    {a.carrierId.slice(0, 8)}
                  </span>
                  <span className="muted small" style={{ flex: 1, color: "var(--k-muted)" }}>
                    {t(`iss.line.${a.lineCode}` as MessageKey)}
                  </span>
                  <StatusPill status={a.status} exceptionType={a.exceptionType} t={t} />
                  <span
                    className="issuance-row-price"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                      color: "var(--k-ink-2)",
                    }}
                  >
                    {a.acceptedPremium} {a.currency}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// The status pill (the `status` field) and, when applicable, a DISTINCT exception chip
// (the `exception_type` field) — two separate fields, never conflated. PAID_NOT_ISSUED
// wears the strong red priority chip (customer-protection); other exceptions read amber.
export function StatusPill({
  status,
  exceptionType,
  t,
}: {
  status: string;
  exceptionType: ExceptionType | null;
  t: (k: MessageKey) => string;
}) {
  return (
    <span
      className="issuance-pills"
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      <span
        className={`k-status k-status--${STATUS_VARIANT[status] ?? "pending"}`}
        data-testid="status-pill"
      >
        <span className="k-status__dot" />
        {t(`iss.status.${status}` as MessageKey)}
      </span>
      {status === "ISSUANCE_EXCEPTION" && exceptionType ? (
        <span
          className={`k-badge ${exceptionType === "PAID_NOT_ISSUED" ? "k-badge--priority" : "k-badge--warning"}`}
          data-testid="exception-type"
        >
          {exceptionType === "PAID_NOT_ISSUED" ? <Icon name="alert-triangle" size={14} /> : null}
          {t(`iss.exception.${exceptionType}` as MessageKey)}
        </span>
      ) : null}
    </span>
  );
}
